<template>
  <div class="tw-flex tw-justify-between tw-gap-10 tw-text-[#1f2229]">
    <div />
    <div class="tw-flex tw-gap-10">
      <div class="tw-bg-white tw-p-12 tw-w-full">
        <AreaHeader />
        <div class="tw-p-10">
          <div>
            <h2 class="tw-font-georgia tw-font-bold tw-text-5xl">Asset Location</h2>
            <AdvisorCoreStripes />
            <p class="tw-mb-4">
              Asset Location is the practice of strategically placing investments in different types of accounts (taxable, tax-deferred, and tax-free) to maximize after-tax returns. 
              In taxable accounts, dividends, interest, and capital gains are taxed annually. 
              In tax-deferred accounts (e.g., IRAs, 401(k)s), taxes on dividends, interest, and gains are deferred until withdrawal. 
              In tax-free accounts (e.g., Roth IRAs), investments grow and can be withdrawn without paying taxes on gains, dividends, or interest.
            </p>
          </div>

          <!-- Replace input section with AssetLocationInput component -->
          <div class="tw-mt-8 tw-mb-4">
            <!-- Tax Impact Section -->
            <div class="tw-mt-6 tw-bg-gray-100 tw-p-4 tw-rounded">
              <h4 class="tw-font-bold tw-text-lg tw-mb-2">Potential Tax Impact</h4>
              <p>
                Estimated Tax Difference At Withdrawal: 
                <span class="tw-font-bold" :class="{'tw-text-green-600': optimizedAllocation.taxSavings > 0, 'tw-text-red-600': optimizedAllocation.taxSavings < 0}">
                  ${{ formatCurrency(optimizedAllocation.taxSavings) }}
                </span>
              </p>
              <p class="tw-text-sm tw-mt-4">
                Methodology: This tax savings estimate is based on optimizing asset location without changing the overall asset allocation. We assume annual growth rates of {{ assumptions.stockGrowthRate }}% for stocks, {{ assumptions.bondYield }}% for bonds, and {{ assumptions.realEstateIncome }}% for real estate and alts over a {{ yearsToWithdrawal }}-year period. The calculation prioritizes the following strategy:
                <ul class="tw-list-disc tw-pl-5 tw-mt-2">
                  <li>Maximizing stocks in tax-free (Roth) accounts</li>
                  <li>Allocating REITs and taxable bonds to tax-deferred accounts</li>
                  <li>Placing remaining stocks and other assets in taxable accounts</li>
                </ul>
              </p>
            </div>
          </div>

          <!-- Account Information and Asset Location Tables -->
          <div class="tw-font-poppins tw-mt-10" v-for="(account, index) in accounts" :key="index">
            <p class="tw-font-georgia tw-font-bold tw-text-3xl tw-mb-4">{{ account.name }}</p>
            <p class="tw-text-lg tw-mb-4">Total Account Value: ${{ formatCurrency(calculateAccountTotal(account)) }}</p>
            <table class="tw-w-full tw-border-collapse tw-mt-6">
              <thead>
                <tr class="tw-bg-[#00345d] tw-text-white">
                  <th></th>
                  <th v-for="asset in assetClasses" :key="asset" class="tw-p-2 tw-text-left">{{ asset }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in ['Current', 'Optimal', 'Difference']" :key="row" :class="rowIndex % 2 === 0 ? 'tw-bg-gray-100' : 'tw-bg-white'">
                  <td class="tw-p-2 tw-font-semibold">{{ row }}</td>
                  <td v-for="asset in assetClasses" :key="asset" class="tw-p-2" 
                      :class="[
                        {'tw-font-bold': isSignificantChange(getAssetValue(account, row, asset).percentage, row)},
                        getDifferenceColor(getAssetValue(account, row, asset).percentage, row)
                      ]">
                    <div>{{ formatValue(getAssetValue(account, row, asset).percentage, row) }}</div>
                    <div class="tw-text-sm tw-text-gray-600">${{ formatCurrency(getAssetValue(account, row, asset).dollarValue) }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="tw-mt-6">
              <p class="tw-font-bold tw-text-xl tw-mb-2">Key Points</p>
              <ul class="tw-list-disc tw-pl-5 tw-space-y-2">
                <li v-for="point in account.keyPoints" :key="point.title" class="tw-text-sm">
                  <span class="tw-font-bold">{{ point.title }}:</span> {{ point.description }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <AreaFooter />
      </div>
      <AreaSidebar
        :area="area"
      >
        <IncludeInPresentationToggle :name="area?.name" />
        <template #other>
        <SpeakersNotes :notes="area?.explanation" />
        <!-- <AIChat /> -->
        <AssetLocationInput
          :incomeTaxRate="incomeTaxRate"
          :ltcgTaxRate="ltcgTaxRate"
          :yearsToWithdrawal="yearsToWithdrawal"
          :assumptions="assumptions"
          @update:incomeTaxRate="incomeTaxRate = $event"
          @update:ltcgTaxRate="ltcgTaxRate = $event"
          @update:yearsToWithdrawal="yearsToWithdrawal = $event"
          @update:assumptions="assumptions = $event"
        />
        </template>
      </AreaSidebar>
    </div>
    <div />
  </div>
</template>

<script>
import AdvisorCoreStripes from '@frontend/ui/features/checkup/components/AdvisorCoreStripes.vue';
import AreaHeader from '@frontend/ui/features/checkup/components/analytics/shared/AreaHeader.vue';
import AreaFooter from '@frontend/ui/features/checkup/components/analytics/shared/AreaFooter.vue';
import AreaSidebar from '@frontend/ui/features/checkup/components/sidebar/AreaSidebar.vue';
import IncludeInPresentationToggle from '@frontend/ui/features/checkup/components/sidebar/IncludeInPresentationToggle.vue';
import SpeakersNotes from '@frontend/ui/features/checkup/components/sidebar/SpeakersNotes.vue';
import AIChat from '@frontend/ui/features/checkup/components/sidebar/AIChat.vue';
import AssetLocationInput from '@frontend/ui/features/checkup/components/AssetLocationInput.vue';
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';

export default {
  name: 'AssetLocationAdvancedArea',
  components: {
    AdvisorCoreStripes,
    AreaHeader,
    AreaFooter,
    AreaSidebar,
    IncludeInPresentationToggle,
    SpeakersNotes,
    AIChat,
    AssetLocationInput,
  },
  props: {
    area: Object,
  },
  data() {
    return {
      assetClasses: ['Stocks', 'Taxable Bonds', 'Municipal Bonds', 'Real Estate and Alts', 'Cash'],
      accounts: [
        {
          name: 'Taxable Accounts',
          current: { 'Stocks': 86, 'Taxable Bonds': 0, 'Municipal Bonds': 0, 'Real Estate and Alts': 2, 'Cash': 2 },
          keyPoints: [
            {
              title: 'Best for Municipal Bonds',
              description: 'Municipal bond interest is tax-free.'
            },
            {
              title: 'OK for Individual Equities or Index Funds',
              description: 'Allows for optimal tax-management strategies'
            },
            {
              title: 'Not appropriate for Taxable Bonds',
              description: 'Taxable (non-municipal) bonds generate interest income, which is generally taxed at a higher rate than capital gains'
            },
          ]
        },
        {
          name: 'Tax-Deferred Accounts',
          current: { 'Stocks': 85, 'Taxable Bonds': 6, 'Municipal Bonds': 0, 'Real Estate and Alts': 3, 'Cash': 6 },
          keyPoints: [
            {
              title: 'Best for Taxable Bonds and Real Estate and Alts',
              description: 'Defer higher-tax interest/dividend income until withdrawal (often at a lower rate in retirement)'
            },
            {
              title: 'Not appropriate for High-Growth Stocks',
              description: 'Tax-deferred accounts do not provide capital gains tax benefits.'
            }
          ]
        },
        {
          name: 'Tax-Free Accounts',
          current: { 'Stocks': 67, 'Taxable Bonds': 0, 'Municipal Bonds': 0, 'Real Estate and Alts': 6, Cash: 27 },
          keyPoints: [
            {
              title: 'Best for High-Growth Stocks',
              description: 'Capital gains are not taxed, and high growth stocks are most likely to create the highest capital gains.'
            },
            {
              title: 'Not appropriate for Bonds or Real Estate and Alts',
              description: 'Tax-Free accounts are best for investments that generate high capital gains (stocks).'
            },
          ]
        }
      ],
      incomeTaxRate: 25,
      ltcgTaxRate: 15,
      yearsToWithdrawal: 10,
      assumptions: {
        stockGrowthRate: 8,
        bondYield: 3,
        realEstateIncome: 5
      },
    };
  },
  computed: {
    ...mapStores(useCheckupStore),
    optimizedAllocation() {
      const portfolioValue = this.checkupStore.portfolio.total_dollars;
      const currentAllocation = {
        taxable: this.convertPercentagesToDollars(this.accounts[0].current, portfolioValue * 0.55),
        taxDeferred: this.convertPercentagesToDollars(this.accounts[1].current, portfolioValue * 0.25),
        taxFree: this.convertPercentagesToDollars(this.accounts[2].current, portfolioValue * 0.20)
      };
      const accountValues = {
        taxable: portfolioValue * 0.55,
        taxDeferred: portfolioValue * 0.25,
        taxFree: portfolioValue * 0.20
      };
      const taxRates = {
        income: this.incomeTaxRate / 100,
        longTermCapitalGains: this.ltcgTaxRate / 100
      };
      const growthRates = {
        stocks: this.assumptions.stockGrowthRate / 100,
        taxableBonds: this.assumptions.bondYield / 100,
        municipalBonds: this.assumptions.bondYield / 100,
        reits: this.assumptions.realEstateIncome / 100
      };

      return this.optimizeAssetLocation(currentAllocation, accountValues, taxRates, growthRates, this.yearsToWithdrawal);
    },
  },
  watch: {
    // Add watchers for the input values
    incomeTaxRate() {
      this.recalculateOptimizedAllocation();
    },
    ltcgTaxRate() {
      this.recalculateOptimizedAllocation();
    },
    yearsToWithdrawal() {
      this.recalculateOptimizedAllocation();
    },
    assumptions: {
      handler() {
        this.recalculateOptimizedAllocation();
      },
      deep: true
    }
  },
  methods: {
    convertPercentagesToDollars(percentages, totalValue) {
      const result = {};
      for (const [asset, percentage] of Object.entries(percentages)) {
        result[asset.toLowerCase().replace(/ /g, '')] = (percentage / 100) * totalValue;
      }
      return result;
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', { 
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },
    calculateAccountTotal(account) {
      const totalValue = this.checkupStore.portfolio.total_dollars;
      const accountProportion = {
        'Taxable Accounts': 0.09458429,
        'Tax-Deferred Accounts': 0.87566743,
        'Tax-Free Accounts': 0.02898551,
      }[account.name] || 1;

      return totalValue * accountProportion;
    },
    
    calculateTaxImpact(allocation, taxRates, growthRates, yearsToWithdrawal) {
      const getFutureValue = (presentValue, rate) => presentValue * Math.pow(1 + rate, yearsToWithdrawal);

      let totalTax = 0;

      // Taxable account
      const taxableStocksFV = getFutureValue(allocation.taxable.stocks, growthRates.stocks);
      const taxableBondsFV = getFutureValue(allocation.taxable.taxablebonds, growthRates.taxableBonds);
      const taxableReitsFV = getFutureValue(allocation.taxable.realestateandalts, growthRates.reits);

      // Tax on stock gains
      totalTax += (taxableStocksFV - allocation.taxable.stocks) * taxRates.longTermCapitalGains;
      
      // Tax on bond income (compounded growth)
      totalTax += (taxableBondsFV - allocation.taxable.taxablebonds) * taxRates.income;

      // Tax on REIT income (compounded growth, taxed as income)
      totalTax += (taxableReitsFV - allocation.taxable.realestateandalts) * taxRates.income;

      // Tax-deferred account (tax on withdrawal, accounting for initial contribution)
      const taxDeferredInitial = Object.values(allocation.taxDeferred).reduce((sum, value) => sum + value, 0);
      const taxDeferredFV = Object.entries(allocation.taxDeferred).reduce((total, [asset, value]) => {
        return total + getFutureValue(value, growthRates[asset] || 0);
      }, 0);
      totalTax += (taxDeferredFV - taxDeferredInitial) * taxRates.income;

      // No tax on tax-free account

      return totalTax;
    },
    optimizeAssetLocation(currentAllocation, accountValues, taxRates, growthRates, yearsToWithdrawal) {
      // Calculate total assets by summing across all accounts
      const totalAssets = {
        stocks: 0,
        taxablebonds: 0,
        municipalbonds: 0,
        realestateandalts: 0,
        cash: 0
      };

      for (const account in currentAllocation) {
        for (const asset in currentAllocation[account]) {
          const normalizedAsset = asset.toLowerCase().replace(/ /g, '');
          totalAssets[normalizedAsset] = (totalAssets[normalizedAsset] || 0) + currentAllocation[account][asset];
        }
      }

      // Initialize optimized allocation object
      const optimizedAllocation = {
        taxable: { stocks: 0, taxablebonds: 0, municipalbonds: 0, realestateandalts: 0, cash: 0 },
        taxDeferred: { stocks: 0, taxablebonds: 0, municipalbonds: 0, realestateandalts: 0, cash: 0 },
        taxFree: { stocks: 0, taxablebonds: 0, municipalbonds: 0, realestateandalts: 0, cash: 0 }
      };

      // Helper: allocate assets to an account
      function allocateAsset(asset, amount, accountType) {
        const available = accountValues[accountType] - Object.values(optimizedAllocation[accountType]).reduce((a, b) => a + b, 0);
        const allocated = Math.min(amount, available);
        optimizedAllocation[accountType][asset] += allocated;
        return amount - allocated;
      }

      // Prioritize allocation: stocks to Tax-Free, bonds/REITs to Tax-Deferred, leftovers to Taxable
      let remainingStocks = allocateAsset('stocks', totalAssets.stocks, 'taxFree');
      let remainingReits = allocateAsset('realestateandalts', totalAssets.realestateandalts, 'taxFree');

      remainingReits = allocateAsset('realestateandalts', remainingReits, 'taxDeferred');
      let remainingBonds = allocateAsset('taxablebonds', totalAssets.taxablebonds, 'taxDeferred');
      remainingStocks = allocateAsset('stocks', remainingStocks, 'taxDeferred');

      // Allocate remaining assets to Taxable account
      optimizedAllocation.taxable.stocks += remainingStocks;
      optimizedAllocation.taxable.taxablebonds += remainingBonds;
      optimizedAllocation.taxable.realestateandalts += remainingReits;
      optimizedAllocation.taxable.municipalbonds = totalAssets.municipalbonds;

      // Allocate cash proportionally across all accounts
      const totalCash = totalAssets.cash;
      Object.keys(accountValues).forEach(account => {
        optimizedAllocation[account].cash = (totalCash * accountValues[account]) / Object.values(accountValues).reduce((a, b) => a + b, 0);
      });

      const currentTaxImpact = this.calculateTaxImpact(currentAllocation, taxRates, growthRates, yearsToWithdrawal);
      const optimizedTaxImpact = this.calculateTaxImpact(optimizedAllocation, taxRates, growthRates, yearsToWithdrawal);

      const totalTaxSavings = currentTaxImpact - optimizedTaxImpact;

      return { 
        optimizedAllocation: optimizedAllocation,
        taxSavings: totalTaxSavings 
      };
    },
    formatValue(value, row) {
      // Round the value to the nearest whole number
      const roundedValue = Math.round(value);
      if (row === 'Difference') {
        return roundedValue > 0 ? `+${roundedValue}%` : `${roundedValue}%`;
      }
      return `${roundedValue}%`;
    },
    isSignificantChange(value, row) {
      return row === 'Difference' && Math.abs(value) >= 5;
    },
    getDifferenceColor(value, row) {
      if (row === 'Difference' && Math.abs(value) >= 5) {
        return value > 0 ? 'tw-text-green-600' : 'tw-text-red-600';
      }
      return '';
    },
    getAssetValue(account, row, asset) {
      const assetKey = asset.toLowerCase().replace(/ /g, '');
      const totalValue = this.checkupStore.portfolio.total_dollars;
      const accountProportion = {
        'Taxable Accounts': 0.55,
        'Tax-Deferred Accounts': 0.25,
        'Tax-Free Accounts': 0.20
      }[account.name] || 1;
      const accountTotal = totalValue * accountProportion;

      if (row === 'Current') {
        const percentage = account.current[asset];
        const dollarValue = (percentage / 100) * accountTotal;
        return { percentage, dollarValue };
      } else if (row === 'Optimal') {
        const accountTypeMap = {
          'Taxable Accounts': 'taxable',
          'Tax-Deferred Accounts': 'taxDeferred',
          'Tax-Free Accounts': 'taxFree'
        };
        const accountType = accountTypeMap[account.name];
        if (!accountType) return { percentage: 0, dollarValue: 0 };
        
        const optimizedValue = this.optimizedAllocation.optimizedAllocation[accountType][assetKey] || 0;
        const percentage = (optimizedValue / accountTotal) * 100;
        return { percentage, dollarValue: optimizedValue };
      } else if (row === 'Difference') {
        const currentValue = this.getAssetValue(account, 'Current', asset);
        const optimizedValue = this.getAssetValue(account, 'Optimal', asset);
        const percentageDiff = optimizedValue.percentage - currentValue.percentage;
        const dollarDiff = optimizedValue.dollarValue - currentValue.dollarValue;
        return { percentage: percentageDiff, dollarValue: dollarDiff };
      }
      return { percentage: 0, dollarValue: 0 };
    },
  }
};
</script>

<style scoped>
table th {
  color: white;
}
ul.tw-list-disc {
  list-style-type: disc;
}
</style>
